import { Link } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import User from '../../services/interfaces/user-interface';
import UserService from '../../services/user-service';
import CircularProgress from '@mui/material/CircularProgress';
import dateTime from 'date-and-time';
import { IoMdArrowRoundForward } from 'react-icons/io';
import Select from 'react-select';
import MultiSelect from './Users/select_fields';
import Swal from 'sweetalert2';
import { AxiosInstance } from '../../common/Loader/axios.config';


interface UserAnalytics {
  totalUsers: number;
  activeUsers: number;
  pendingUsers: number;
  deactivatedUsers: number;
  newUsersToday: number;
  newUsersThisMonth: number;
}

const ManageUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [filterText, setFilterText] = useState<string>('');

  const itemsPerPage = 50;
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);

  const [status, setStatus] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<any>(null);

  const [allCount, setAllCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [authorizedUsers, setAuthorizedUsers] = useState<User[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [deactivatedCount, setDeactivatedCount] = useState(0);
  const [googleCount, setGoogleCount] = useState(0);
  const [appleCount, setAppleCount] = useState(0);

  const [stats, setStats] = useState<UserAnalytics | null>(null);

  const [columnWidths, setColumnWidths] = useState({
    Date: 70,
    User: 150,
    UserID: 150,
    Email: 200,
    Phone: 50,
    Region: 50,
    AppleId: 150,
    Location: 100,
    Country: 50,
    ISP: 50,
    Status: 50,
    Action: 50,
  });

  const resizeHandleRef = useRef<HTMLDivElement | null>(null);

  const [selectedFields, setSelectedFields] = useState([
    'Date',
    'User',
    'UserID',
    'Email',
    'Phone',
    'Region',
    'Status',
  ]);

  const isGoogleAuth = (appleid?: string) => !!appleid && /^\d{21}$/.test(appleid);
  const isAppleAuth = (appleid?: string) => !!appleid && /^\d{6}\..+\..+$/.test(appleid);

  useEffect(() => {
    async function fetchUsers() {
      setLoading(true);

      try {
        const skip = (currentPage - 1) * itemsPerPage;
        const response = await UserService.getUsers(itemsPerPage, skip);

        const pageUsers = response.users ?? [];

        setUsers(pageUsers);
        setFilteredUsers(pageUsers);

        // if server returns fewer than requested, there is no next page
        setHasMore(pageUsers.length === itemsPerPage);

        const uniqueStatus = [
          ...new Set(pageUsers.map((user) => user.status?.admin).filter(Boolean)),
        ] as string[];
        setStatus(uniqueStatus);

        // counts below are for the current page only
        setAllCount(pageUsers.length);

        const activeUsersCount = pageUsers.filter(
          (user) => user.status?.admin === 'active' || user.status?.admin === 'authorized'
        ).length;
        setActiveCount(activeUsersCount);

        const authorizedUsersList = pageUsers.filter(
          (user) => user.status?.admin === 'authorized'
        );
        setAuthorizedUsers(authorizedUsersList);

        const googleUsersCount = authorizedUsersList.filter(
          (user) => isGoogleAuth(user.appleid)
        ).length;

        const appleUsersCount = authorizedUsersList.filter(
          (user) => isAppleAuth(user.appleid)
        ).length;

        setGoogleCount(googleUsersCount);
        setAppleCount(appleUsersCount);

        const deactivatedUsersCount = pageUsers.filter(
          (user) => user.status?.admin === 'deactivated'
        ).length;
        setDeactivatedCount(deactivatedUsersCount);

        const pendingUsersCount = pageUsers.filter(
          (user) => user.status?.admin === 'pending'
        ).length;
        setPendingCount(pendingUsersCount);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchUsers();
  }, [currentPage]);

    useEffect(() => {
      const fetchUserAnalytics = async () => {
        try {
          const response = await AxiosInstance.get('/users/a/analytics');
  
          if (response.data?.status === 'success') {
            setStats(response.data.message);
            console.log('User Analytics:', response.data.message);
          }
        } catch (error) {
          console.error('Error fetching user analytics:', error);
        }
      };
  
      fetchUserAnalytics();
    }, []);

  useEffect(() => {
    const search = filterText.toLowerCase();

    let filtered = users.filter((user) => {
      const emailMatch = user.email?.toLowerCase().includes(search);
      const phoneMatch = user.phone?.toLowerCase().includes(search);
      const nameMatch = user.username?.toLowerCase().includes(search);

      return emailMatch || phoneMatch || nameMatch;
    });

    if (selectedStatus?.value && selectedStatus.value !== 'all') {
      filtered = filtered.filter((user) => user.status?.admin === selectedStatus.value);
    }

    setFilteredUsers(filtered);
  }, [users, filterText, selectedStatus]);

  useEffect(() => {
    if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [currentPage]);

  const handleSort = (column: keyof User) => {
    const sortedUsers = [...filteredUsers].sort((a: any, b: any) => {
      if (a[column] < b[column]) return -1;
      if (a[column] > b[column]) return 1;
      return 0;
    });
    setFilteredUsers(sortedUsers);
  };

  const statusOptions = [
    { value: 'all', label: 'All' },
    ...status.map((statusItem) => ({
      value: statusItem,
      label: statusItem,
    })),
  ];

  const handleStatusChange = (option: any) => {
    setSelectedStatus(option);
  };

  const handleTypeChange = (option: any) => {
    if (option.value === 'all') {
      setFilteredUsers(users);
    } else if (option.value === 'google') {
      const filtered = authorizedUsers.filter((user) => isGoogleAuth(user.appleid));
      setFilteredUsers(filtered);
    } else if (option.value === 'apple') {
      const filtered = authorizedUsers.filter((user) => isAppleAuth(user.appleid));
      setFilteredUsers(filtered);
    }
  };

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>, columnName: string) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = columnWidths[columnName as keyof typeof columnWidths];

    let animationFrameId: number;

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        const newWidth = Math.max(50, startWidth + (moveEvent.clientX - startX));
        setColumnWidths((prevWidths) => ({
          ...prevWidths,
          [columnName]: newWidth,
        }));
      });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);

      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const handleFieldChange = (updatedFields: string[]) => {
    setSelectedFields(updatedFields);
  };

  const handleDeleteAccount = async (userId: string) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete User!',
    });

    if (result.isConfirmed) {
      try {
        const response = await AxiosInstance.delete(`/users/${userId}`);
        if (response.status === 200) {
          Swal.fire({
            icon: 'success',
            title: 'User deleted successfully!',
            showConfirmButton: false,
            timer: 2000,
          });

          setUsers((prev) => prev.filter((user) => user._id !== userId));
          setFilteredUsers((prev) => prev.filter((user) => user._id !== userId));
        } else {
          console.error('Failed to delete User', response.data);
        }
      } catch (error) {
        console.error('Failed to delete User', error);
        Swal.fire({
          icon: 'error',
          title: 'Failed to delete User!',
          text: 'Please try again later.',
        });
      }
    }
  };

  return (
    <div className="w-[1200px] rounded-sm bg-transparent px-2 pb-1.5 pt-6 dark:border-strokedark sm:px-7.5 xl:pb-1">
      <div className="mb-4 flex flex-col text-center">
        <div>
          <div className="flex items-center justify-between">
            <div className="text-black border border-brand-stroke-color rounded-lg p-1" id="select">
              <MultiSelect
                options={Object.keys(columnWidths).filter((key) => key !== 'Action')}
                label="Select Fields"
                selectedOptions={selectedFields}
                onChange={handleFieldChange}
              />
            </div>

            <input
              type="text"
              placeholder="Search User..."
              className="w-full rounded-md border border-brand-stroke-color bg-transparent px-3 py-2 text-white sm:w-2/3 md:w-1/2 lg:w-1/3"
              value={filterText}
              onChange={(event) => {
                setFilterText(event.target.value);
              }}
            />
          </div>

          <div className="flex items-center justify-between my-2">
            <div className="flex items-center gap-x-4 flex-wrap">
              <div
                className="text-white text-xs border border-white px-4 py-2 rounded-lg w-fit font-bold my-2 cursor-pointer hover:scale-105"
                onClick={() => {
                  setSelectedStatus({ value: 'all', label: 'All' });
                  setFilteredUsers(users);
                }}
              >
                All: {stats?.totalUsers ?? 0}
              </div>

              <div
                className="text-success text-xs border border-success px-4 py-2 rounded-lg w-fit font-bold my-2 cursor-pointer hover:scale-105"
                onClick={() => setSelectedStatus({ value: 'active', label: 'Active' })}
              >
                Active: {stats?.activeUsers ?? 0}
              </div>

              <div
                className="text-warning text-xs border border-warning px-4 py-2 rounded-lg w-fit font-bold my-2 cursor-pointer hover:scale-105"
                onClick={() => setSelectedStatus({ value: 'pending', label: 'Pending' })}
              >
                Pending: {stats?.pendingUsers ?? 0}
              </div>

              <div
                className="text-danger text-xs border border-danger px-4 py-2 rounded-lg w-fit font-bold my-2 cursor-pointer hover:scale-105"
                onClick={() => setSelectedStatus({ value: 'deactivated', label: 'Deactivated' })}
              >
                Deactivated: {stats?.deactivatedUsers ?? 0}
              </div>

              {/* <div
                className="text-success text-xs border border-success px-4 py-2 rounded-lg w-fit font-bold my-2 cursor-pointer hover:scale-105"
                onClick={() => handleTypeChange({ value: 'google' })}
              >
                Google: {googleCount}
              </div>

              <div
                className="text-success text-xs border border-success px-4 py-2 rounded-lg w-fit font-bold my-2 cursor-pointer hover:scale-105"
                onClick={() => handleTypeChange({ value: 'apple' })}
              >
                Apple: {appleCount}
              </div> */}
            </div>

            <div className="flex items-center gap-x-2">
              <p className="text-white text-sm">Sort by:</p>

              <div className="w-[40vh] text-black border border-brand-stroke-color rounded-lg p-1" id="select">
                <Select
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  options={statusOptions}
                  placeholder="Select Status..."
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="table-auto w-full bg-brand-background shadow-md rounded-lg">
              <thead>
                <tr className="text-white text-sm text-center">
                  {selectedFields.map((columnName) => (
                    <th
                      key={columnName}
                      className="p-4 relative border-r border-brand-stroke-color"
                      style={{ width: `${columnWidths[columnName as keyof typeof columnWidths]}px` }}
                    >
                      <button type="button" onClick={() => handleSort(columnName as keyof User)}>
                        {columnName}
                      </button>

                      <div
                        ref={resizeHandleRef}
                        className="resize-handle absolute right-0 top-0 h-full w-2 cursor-col-resize"
                        onMouseDown={(e) => onMouseDown(e, columnName)}
                      />
                    </th>
                  ))}

                  <th
                    key="Action"
                    className="p-4 relative border-r border-brand-stroke-color"
                    style={{ width: `${columnWidths.Action}px` }}
                  >
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={selectedFields.length + 1} className="py-4 text-center">
                      <CircularProgress />
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={selectedFields.length + 1} className="py-4 text-center text-white">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-100 text-white text-xs text-center">
                      {selectedFields.map((columnName) => (
                        <td key={columnName} className="py-4 px-1">
                          {columnName === 'Date' &&
                            dateTime.format(new Date(Date.parse(user.created_at)), 'DD/MM/YY HH:mm ')}

                          {columnName === 'User' && user.username}
                          {columnName === 'UserID' && user._id}
                          {columnName === 'AppleId' && user.appleid}
                          {columnName === 'Email' && user.email}
                          {columnName === 'Phone' && user.phone}
                          {columnName === 'Region' && user.region}
                          {columnName === 'Country' && user.country}

                          {columnName === 'Location' && (
                            <div className="flex items-center justify-center gap-x-2">
                              <p>{user.city}</p>
                              <p>{user.country}</p>
                            </div>
                          )}

                          {columnName === 'ISP' && user.isp}

                          {columnName === 'Status' && (
                            <p
                              className={`text-${user.status?.admin === 'active'
                                ? 'success'
                                : user.status?.admin === 'authorized'
                                  ? 'success'
                                  : user.status?.admin === 'pending'
                                    ? 'warning'
                                    : 'danger'
                                }`}
                            >
                              {user.status?.admin}
                            </p>
                          )}
                        </td>
                      ))}

                      <td key="Action" className="py-4 px-1">
                        <div className="flex items-center justify-center gap-x-1">
                          <Link to={`/user?JSXKU=${user?._id || ''}`}>
                            <IoMdArrowRoundForward className="ml-6" size={18} />
                          </Link>

                          {/* <button onClick={() => handleDeleteAccount(user._id)}>
                            Delete
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-white">
              Page {currentPage}
            </p>

            <div className="flex items-center gap-2">
              <button
                className="rounded border border-brand-stroke-color px-4 py-2 text-white disabled:opacity-50"
                disabled={currentPage === 1 || loading}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                Previous
              </button>

              <button
                className="rounded border border-brand-stroke-color px-4 py-2 text-white disabled:opacity-50"
                disabled={!hasMore || loading}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageUsers;